import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SelectionModel } from '@angular/cdk/collections';
import { stringEquals } from '@utils/string.util';
import { FilterService } from '@services/filter.service';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';

/** 設定テンプレートモデル */
export type AssetTypeModel = {
  /** 設定テンプレートID */
  assettypeId: string;
  /** 設定テンプレート名 */
  assettypeName: string;
  /** テナント名 */
  tenantName: string;
  /** テナントID */
  tenantId: string;
  /** デバイスステータス */
  deviceActiveStatus: boolean;
  /** デバイス種類ID */
  deviceTypeId: string;
  /** デバイス種類内部キー */
  deviceTypeKey: string;
  /** デバイス種類表示名 */
  deviceTypeDisplayName?: string;
  /** 測定間隔 */
  samplingFrequency: number;
  /** 送信間隔 */
  reportingFrequency: number;
  /** 位置検知 */
  locationDetected: boolean;
  /** 位置測位技術優先度1 */
  locationPriority_01: string;
  /** 位置測位技術優先度2 */
  locationPriority_02: string;
  /** 位置測位技術優先度3 */
  locationPriority_03: string;
  /** 落下検知イベント */
  fallDetected: boolean;
  /** 動作検知イベント */
  mcdDetected: boolean;
  /** 明暗検知イベント */
  lightExposureDetected: boolean;
  /** 明暗検知イベント設定値 */
  lightExposureDetectValue: string;
  /** 衝撃検知イベント */
  shockDetected: boolean;
  /** 衝撃検知イベント設定値 */
  shockDetectedDetectValue: number;
  /** 温度センサー */
  temperature: boolean;
  /** 温度アラート上限逸脱 */
  temperatureAlertMax: number;
  /** 温度アラート下限逸脱 */
  temperatureAlertMin: number;
  /** 温度アラート上限警告 */
  temperaturePrealertMax: number;
  /** 温度アラート下限警告 */
  temperaturePrealertMin: number;
  /** 気圧センサー */
  pressure: boolean;
  /** 気圧センサー上限逸脱 */
  pressureAlertMax: number;
  /** 気圧センサー下限逸脱 */
  pressureAlertMin: number;
  /** 気圧センサー上限警告 */
  pressurePrealertMax: number;
  /** 気圧センサー下限警告 */
  pressurePrealertMin: number;
  /** 湿度センサー */
  humidity: boolean;
  /** 湿度センサー上限逸脱 */
  humidityAlertMax: number;
  /** 湿度センサー下限逸脱 */
  humidityAlertMin: number;
  /** 湿度センサー上限警告 */
  humidityPrealertMax: number;
  /** 湿度センサー下限警告 */
  humidityPrealertMin: number;
  /** 傾斜センサー */
  tilt: boolean;
  /** 傾斜センサー上限逸脱 */
  tiltAlertMax: number;
  /** 傾斜センサー下限逸脱 */
  tiltAlertMin: number;
  /** 傾斜センサー上限警告 */
  tiltPrealertMax: number;
  /** 傾斜センサー下限警告 */
  tiltPrealertMin: number;
  /** 照度センサー */
  light: boolean;
  /** 照度センサー上限逸脱 */
  lightAlertMax: number;
  /** 照度センサー下限逸脱 */
  lightAlertMin: number;
  /** 照度センサー上限警告 */
  lightPrealertMax: number;
  /** 照度センサー下限警告 */
  lightPrealertMin: number;
  /** フライトモード */
  fmd: boolean;
  /** 配送機能 */
  shipmentTracking: boolean;
  /** 作成者 */
  createdBy: string;
  /** 更新者 */
  updatedBy: string;
  /** 作成日時 */
  createdAt: string;
  /** 更新日時 */
  updatedAt: string;
};

/** 設定テンプレートテーブルカラム */
export const ASSET_TYPE_TABLE_COLUMN_LIST: string[] = [
  'checkbox',
  'assettypeName',
  'deviceTypeId',
  'updatedAt',
  'updatedBy',
  'operation',
] as const;

interface AssetTypeFilter {
  assetTypeName?: string;
  deviceTypeDisplayName?: string | null;
  tenantName?: string;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  sortState?: Sort;
  pageIndex?: number;
}

const pageId = 'SettingTemplate';

@Component({
  selector: 'app-asset-type-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    DatePipe,
    DirectivesModule,
  ],
  templateUrl: './asset-type-list-table.component.html',
  styleUrl: './asset-type-list-table.component.scss',
})
export class AssetTypeListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  /** ボタンの無効化状態 */
  @Input({ required: true }) isDisabled: boolean = false;

  /** 設定テンプレート一覧 */
  @Input({ required: true }) assetTypeList: AssetTypeModel[] = [];

  /** デバイス種類のリスト */
  @Input() public deviceTypes: string[] = [];

  /** 新規登録を通知 */
  @Output() created = new EventEmitter<void>();

  /** 更新を通知 */
  @Output() updated = new EventEmitter<AssetTypeModel>();

  /** 削除を通知 */
  @Output() deleted = new EventEmitter<AssetTypeModel[]>();

  /** コピーを通知 */
  @Output() copied = new EventEmitter<AssetTypeModel>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** カラム */
  public displayedColumns: string[] = ASSET_TYPE_TABLE_COLUMN_LIST;

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<AssetTypeModel>([]);

  /** 選択状態 */
  public selection = new SelectionModel<AssetTypeModel>(true, []);

  /** 一括削除ボタン表示状態 */
  public isBulkDeleteButtonDisable: boolean = true;

  /** 選択されたデバイス種類 */
  public selectedDeviceType: string | null = null;

  /** 設定テンプレート名検索キーワード */
  public assetTypeNameKeyword = '';

  /** テナント名検索キーワード */
  public tenantNameKeyword = '';

  /** 作成日時検索キーワード */
  public createdAtKeyword = '';

  /** 作成者検索キーワード */
  public createdByKeyword = '';

  /** 最終更新日時検索キーワード */
  public updatedAtKeyword = '';

  /** 更新者検索キーワード */
  public updatedByKeyword = '';

  /** 初期ソート値 */
  public initSortState: Sort = {
    active: 'assettypeName',
    direction: 'asc',
  };

  /** ソート状態 */
  private sortState: Sort | undefined;

  /** ページ番号 */
  private pageIndex: number | undefined;

  constructor(
    private filterService: FilterService<AssetTypeFilter, AssetTypeModel>,
    private cdr: ChangeDetectorRef,
  ) {
    this.filterService.setSessionStorageKey(pageId);
  }

  public ngOnInit(): void {
    this.dataSource.data = this.assetTypeList;
    this.dataSource.sort = this.sort;

    // フィルター条件の取得と適用
    this.loadFilters();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      if (property === 'deviceTypeId') return item.deviceTypeDisplayName || '';
      const value = item[property as keyof AssetTypeModel];
      return typeof value === 'number' ? value : String(value ?? '');
    };

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソートとページングの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }

    // フィルター設定
    this.dataSource.filterPredicate = (element: AssetTypeModel) => {
      let assetTypeName = true;
      if (this.assetTypeNameKeyword) {
        // assetTypeNameKeywordが空でない場合のみフィルタリングを行う
        assetTypeName =
          element.assettypeName
            .trim()
            .toLowerCase()
            .indexOf(this.assetTypeNameKeyword.toLowerCase()) > -1;
      }
      let deviceTypeDisplayName = true;
      if (this.selectedDeviceType) {
        deviceTypeDisplayName =
          element.deviceTypeDisplayName === this.selectedDeviceType;
      }
      let tenantName = true;
      if (this.tenantNameKeyword) {
        // tenantNameKeywordが空でない場合のみフィルタリングを行う
        tenantName =
          element.tenantName
            .trim()
            .toLowerCase()
            .indexOf(this.tenantNameKeyword.toLowerCase()) > -1;
      }
      let createdAt = true;
      if (this.createdAtKeyword) {
        // createdAtKeywordが空でない場合のみフィルタリングを行う
        createdAt =
          element.createdAt === undefined || element.createdAt === null
            ? false
            : stringEquals(
                `${new Date(element.createdAt).getFullYear()}/${String(
                  new Date(element.createdAt).getMonth() + 1,
                ).padStart(2, '0')}/${String(
                  new Date(element.createdAt).getDate(),
                ).padStart(2, '0')}`,
                this.createdAtKeyword,
              );
      }
      let createdBy = true;
      if (this.createdByKeyword) {
        // createdByKeywordが空でない場合のみフィルタリングを行う
        createdBy =
          element.createdBy
            .trim()
            .toLowerCase()
            .indexOf(this.createdByKeyword.toLowerCase()) > -1;
      }
      let updatedAt = true;
      if (this.updatedAtKeyword) {
        // updatedAtKeywordが空でない場合のみフィルタリングを行う
        updatedAt =
          element.updatedAt === undefined || element.updatedAt === null
            ? false
            : stringEquals(
                `${new Date(element.updatedAt).getFullYear()}/${String(
                  new Date(element.updatedAt).getMonth() + 1,
                ).padStart(2, '0')}/${String(
                  new Date(element.updatedAt).getDate(),
                ).padStart(2, '0')}`,
                this.updatedAtKeyword,
              );
      }
      let updatedBy = true;
      if (this.updatedByKeyword) {
        // updatedByKeywordが空でない場合のみフィルタリングを行う
        updatedBy =
          element.updatedBy
            .trim()
            .toLowerCase()
            .indexOf(this.updatedByKeyword.toLowerCase()) > -1;
      }
      // フィルター条件を保持
      this.saveFiltersToStorage();

      return (
        assetTypeName &&
        deviceTypeDisplayName &&
        tenantName &&
        createdAt &&
        createdBy &&
        updatedAt &&
        updatedBy
      );
    };

    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['assetTypeList'] !== undefined) {
      const assetTypeList: AssetTypeModel[] =
        changes['assetTypeList'].currentValue;
      this.dataSource.data = assetTypeList;

      // 設定テンプレート一覧が更新された場合、選択情報をクリア
      this.selection.clear();

      // 一括削除ボタンの表示判定
      this.updateBulkDeleteButtonState();
    }
  }

  /**
   * すべての行を選択／非選択に切り替える
   */
  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this.dataSource.filteredData);
  }

  /**
   * すべて選択されているか
   *
   * @returns すべて選択されていればtrue, 非選択があればfalse
   */
  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /**
   * 一括削除ボタン押下処理
   */
  public bulkDelete() {
    this.deleted.emit(this.selection.selected);
  }

  /** 一括削除ボタンの表示状態管理 */
  public updateBulkDeleteButtonState(): void {
    this.isBulkDeleteButtonDisable = this.selection.selected.length === 0;
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * 設定テンプレート名でのフィルタリングをクリアする。
   */
  public clearAssetTypeNameFilter(): void {
    // フィルタ条件をクリア
    this.assetTypeNameKeyword = '';

    this.applyFilter();
  }

  /**
   * テナント名でのフィルタリングをクリアする。
   */
  public clearTenantNameFilter(): void {
    // フィルタ条件をクリア
    this.tenantNameKeyword = '';

    this.applyFilter();
  }

  /**
   * 作成日時でのフィルタリングをクリアする。
   */
  public clearCreatedAtFilter(): void {
    // フィルタ条件をクリア
    this.createdAtKeyword = '';

    this.applyFilter();
  }

  /**
   * 作成者でのフィルタリングをクリアする。
   */
  public clearCreatedByFilter(): void {
    // フィルタ条件をクリア
    this.createdByKeyword = '';

    this.applyFilter();
  }

  /**
   * 最終更新日時でのフィルタリングをクリアする。
   */
  public clearUpdatedAtFilter(): void {
    // フィルタ条件をクリア
    this.updatedAtKeyword = '';

    this.applyFilter();
  }

  /**
   * 更新者でのフィルタリングをクリアする。
   */
  public clearUpdatedByFilter(): void {
    // フィルタ条件をクリア
    this.updatedByKeyword = '';

    this.applyFilter();
  }

  /**
   * 各フィルターを初期化する。
   */
  public clearFilters() {
    // フィルターを初期化
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    this.setPaginationIndex();

    // フィルターを適用
    this.dataSource.filter = '';
    this.applyFilter();
  }

  /**
   * ページネーションの番号を設定する
   */
  private setPaginationIndex(pageIndex: number = 0) {
    setTimeout(() => {
      this.paginator.pageIndex = pageIndex!;
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(reset?: boolean): void {
    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.assetTypeNameKeyword = filters.assetTypeName || '';
    this.selectedDeviceType = filters.deviceTypeDisplayName || '';
    this.tenantNameKeyword = filters.tenantName || '';
    this.createdAtKeyword = filters.createdAt || '';
    this.createdByKeyword = filters.createdBy || '';
    this.updatedAtKeyword = filters.updatedAt || '';
    this.updatedByKeyword = filters.updatedBy || '';
    this.sortState = filters.sortState;
    this.pageIndex = filters.pageIndex;
  }

  /**
   * フィルター条件をセッションストレージに保存する
   */
  private saveFiltersToStorage(): void {
    const filterList: AssetTypeFilter = {
      assetTypeName: this.assetTypeNameKeyword,
      deviceTypeDisplayName: this.selectedDeviceType,
      tenantName: this.tenantNameKeyword,
      createdAt: this.createdAtKeyword,
      createdBy: this.createdByKeyword,
      updatedAt: this.updatedAtKeyword,
      updatedBy: this.updatedByKeyword,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }
}
